/**
 * Support vocabularies — runtime mirrors of the contract enums.
 *
 * The console imports contract *types* only (type-only re-exports in `types.ts`), so it
 * never bundles the contracts package or its runtime deps. Where a page needs the actual
 * list of values to render a filter or a picker, it reads this thin mirror — typed with
 * `satisfies` against the real unions, so dropping or renaming a value in the contract
 * fails this file's compile rather than drifting silently.
 */
import type {
  BroadcastAudienceKind,
  BroadcastCategory,
  BroadcastStatus,
  EmailCategory,
  EmailImageMime,
  EmailTemplateKey,
  GiftcardCategory,
  GiftcardFaceCurrency,
  KycTier,
  PlatformSettings,
  PushCategory,
  SenderType,
  SupportPriority,
  SupportStatus,
  SupportThreadType,
} from './types.js';

export const SUPPORT_STATUSES = ['open', 'pending', 'resolved', 'closed'] as const satisfies readonly SupportStatus[];

export const SUPPORT_PRIORITIES = [
  'low',
  'normal',
  'high',
  'urgent',
] as const satisfies readonly SupportPriority[];

export const SUPPORT_THREAD_TYPES = ['chat', 'ticket'] as const satisfies readonly SupportThreadType[];

export const SENDER_TYPES = ['customer', 'admin', 'system'] as const satisfies readonly SenderType[];

// ── Business Settings / white-label ──────────────────────────────────────────

/**
 * The ledger currencies the platform can default to. Mirrors `@genex/money`'s codes.
 *
 * `satisfies` only proves every entry is a real currency; it cannot prove none is missing, and
 * one was. USDC was absent while the money core has carried it for as long as USDT, it has two
 * deposit networks in the shared catalogue, and the server ships it in the default set of
 * currencies the Convert screen offers. The effect was that an operator could not pick USDC as
 * the platform's default currency, and could not put it back on the Convert screen once they
 * had taken it off — the tick box for it did not exist.
 */
export const CURRENCY_CODES = [
  'NGN',
  'USD',
  'BTC',
  'ETH',
  'SOL',
  'USDT',
  'USDC',
] as const satisfies readonly PlatformSettings['defaultCurrency'][];

/** The eight brand social networks an operator may wire, in the order the editor shows them. */
export const SOCIAL_NETWORKS = [
  'twitter',
  'facebook',
  'instagram',
  'linkedin',
  'telegram',
  'whatsapp',
  'tiktok',
  'youtube',
] as const;

// ── Email templates ──────────────────────────────────────────────────────────

/**
 * Every transactional message the platform sends, by key. Mirrors `EMAIL_TEMPLATE_KEYS`.
 *
 * The last three had been added to the contract and never here, and `satisfies` cannot catch
 * that: it checks that each entry is a real key, not that every real key is an entry.
 */
export const EMAIL_TEMPLATE_KEYS = [
  'welcome',
  'email_otp',
  'password_reset',
  'receipt_sell',
  'receipt_convert',
  'receipt_withdraw',
  'receipt_transfer',
  'receipt_funding',
  'receipt_giftcard',
  'support_reply',
  'missed_chat',
  'security_alert',
  /** The code that lets a device this account has never used finish signing in. */
  'device_verification',
  /** Somebody signed into this account. The ordinary case, not the alarm above. */
  'login_alert',
  /** The only one whose recipient is the operator rather than a customer. */
  'admin_platform_event',
] as const satisfies readonly EmailTemplateKey[];

/**
 * The tokens every template may use, filled by the server on every render.
 *
 * The first six come from `platform_settings`; the seven `layout*` ones come from the active
 * email layout. The layout palette is exposed as tokens for a reason worth keeping in view: the
 * shipped bodies used to write `color:#101828` into every heading, so an operator who chose a
 * dark layout got dark text on a dark card and nothing in the system noticed. The palette
 * validator guards the layout's own pairs; it cannot guard a hex literal typed into a body.
 *
 * Globals win over any caller-supplied variable, so a variable map cannot repaint the brand.
 */
export const EMAIL_GLOBAL_VARIABLES = [
  'logo',
  'platformName',
  'primaryColor',
  'accentColor',
  'supportEmail',
  'websiteUrl',
  'layoutHeading',
  'layoutBody',
  'layoutMuted',
  'layoutLink',
  'layoutButtonBg',
  'layoutButtonText',
  'layoutCardBg',
] as const;

/** Whether a message is one a customer may opt out of. Mirrors `EMAIL_CATEGORIES`. */
export const EMAIL_CATEGORIES = [
  'transactional',
  'marketing',
] as const satisfies readonly EmailCategory[];

/** What may be uploaded for use in mail. No SVG: see the contract's note. */
export const EMAIL_IMAGE_MIMES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const satisfies readonly EmailImageMime[];

// ── Broadcasts ───────────────────────────────────────────────────────────────

/**
 * The customer classes a broadcast may address. Mirrors `BROADCAST_AUDIENCE_KINDS`.
 *
 * A closed list rather than a query language, because a console that could express arbitrary
 * predicates over the customer table would be a data-exfiltration tool with a Send button.
 */
export const BROADCAST_AUDIENCE_KINDS = [
  'users',
  'all',
  'verified',
  'unverified',
  'tier',
  'signed_up_between',
] as const satisfies readonly BroadcastAudienceKind[];

/** Both are non-transactional and both honour an unsubscribe. Mirrors `BROADCAST_CATEGORIES`. */
export const BROADCAST_CATEGORIES = [
  'marketing',
  'announcement',
] as const satisfies readonly BroadcastCategory[];

export const BROADCAST_STATUSES = [
  'draft',
  'queued',
  'sending',
  'completed',
  'cancelled',
  'failed',
] as const satisfies readonly BroadcastStatus[];

// ── Push ─────────────────────────────────────────────────────────────────────

/** The four notification categories a broadcast can target. Mirrors `PUSH_CATEGORIES`. */
export const PUSH_CATEGORIES = [
  'money',
  'support',
  'security',
  'marketing',
] as const satisfies readonly PushCategory[];

// ── Gift cards ───────────────────────────────────────────────────────────────

/** The card categories the app groups the catalogue by. Mirrors `GIFTCARD_CATEGORIES`. */
export const GIFTCARD_CATEGORIES = [
  'shopping',
  'entertainment',
  'gaming',
  'other',
] as const satisfies readonly GiftcardCategory[];

/**
 * The face currencies a brand may be denominated in. Mirrors `GIFTCARD_FACE_CURRENCIES` —
 * a small closed set of foreign fiats the *ledger does not hold*, which is why they are not
 * `CURRENCY_CODES` above and why a brand's face money is its own DTO.
 */
export const GIFTCARD_FACE_CURRENCIES = [
  'USD',
  'GBP',
  'EUR',
  'CAD',
  'AUD',
] as const satisfies readonly GiftcardFaceCurrency[];

// ── KYC tiers ────────────────────────────────────────────────────────────────

/** The four account tiers, lowest first. Mirrors `KYC_TIERS`. */
export const KYC_TIERS = ['tier_0', 'tier_1', 'tier_2', 'tier_3'] as const satisfies readonly KycTier[];

/**
 * What each tier is called on a screen. Mirrors `TIER_REQUIREMENTS`' companion in the contract.
 *
 * `tier_0` is the whole reason this exists. It is a real state - registered, nothing proved -
 * and it has to be nameable, but calling it "Tier 0" puts a rung on the ladder that is not one:
 * an operator scanning a user list reads a column of "Tier 0" as a population rather than as a
 * queue of people who have not started.
 */
export const TIER_LABELS: Readonly<Record<KycTier, string>> = Object.freeze({
  tier_0: 'Not verified yet',
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  tier_3: 'Tier 3',
});
